// Importaciones de React y componentes MUI
import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import MKBox from 'components/MKBox';
import MKTypography from 'components/MKTypography';
import MKButton from 'components/MKButton';

import { formatMonthYear, secondaryButtonSx } from '../utils/constants';

function ResumeView({
  resumeData,
  setResumeData,
  setOpenExp,
  setOpenEdu,
  handleDeleteExperience,
  handleDeleteEducation,
  handleGuardarTodo,
  setActiveTab,
  loading,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0 && files[0].type === 'application/pdf') {
      setResumeData((prev) => ({ ...prev, file: files[0] }));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setResumeData((prev) => ({ ...prev, file: e.target.files[0] }));
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setResumeData((prev) => ({ ...prev, file: null }));
  };

  return (
    <MKBox width="100%">
      <Grid container spacing={3}>
        {/* --- ACORDEÓN PARA CARGA DE ARCHIVO --- */}
        <Grid item xs={12}>
          <MKBox
            onClick={() => setShowUpload(!showUpload)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderRadius: 2,
              bgcolor: '#f8f9fa',
              cursor: 'pointer',
              border: '1px solid #e0e0e0',
              '&:hover': { bgcolor: '#eff2f5' },
            }}
          >
            <MKBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ color: '#1a73e8' }}>attachment</Icon>
              <MKTypography variant="button" fontWeight="bold" color="text">
                {resumeData.file ? 'Archivo adjunto' : 'Adjuntar Hoja de Vida (PDF)'}
              </MKTypography>
            </MKBox>
            <ExpandMoreIcon
              sx={{
                transform: showUpload ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                color: '#7b809a',
              }}
            />
          </MKBox>

          <Collapse in={showUpload}>
            <MKBox
              mt={2}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              component="label"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 5,
                border: '1px dashed',
                borderRadius: 'xl',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderColor: isDragging ? '#1A73E8' : 'rgba(145, 158, 171, 0.32)',
                bgcolor: isDragging ? 'rgba(26, 115, 232, 0.08)' : 'rgba(145, 158, 171, 0.04)',
                '&:hover': { bgcolor: 'rgba(26, 115, 232, 0.08)', borderColor: '#1A73E8' },
              }}
            >
              <input hidden accept="application/pdf" type="file" onChange={handleFileSelect} />

              {resumeData.file ? (
                <MKBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  p={2}
                  sx={{
                    bgcolor: 'rgba(26, 115, 232, 0.1)',
                    borderRadius: 2,
                    border: '1px solid #1A73E8',
                  }}
                >
                  <MKBox display="flex" alignItems="center" gap={2}>
                    <InsertDriveFileIcon sx={{ color: '#1A73E8', fontSize: 40 }} />
                    <MKBox>
                      <MKTypography variant="h6" fontWeight="bold" color="text">
                        {resumeData.file.name}
                      </MKTypography>
                      <MKTypography variant="caption" color="text">
                        {(resumeData.file.size / 1024 / 1024).toFixed(2)} MB
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                  <IconButton onClick={removeFile} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </MKBox>
              ) : (
                <>
                  <MKBox
                    mb={2}
                    sx={{
                      p: 1.5,
                      borderRadius: '50%',
                      bgcolor: 'rgba(26, 115, 232, 0.1)',
                      color: '#1A73E8',
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 40 }} />
                  </MKBox>
                  <MKTypography variant="h5" fontWeight="bold" color="text">
                    Selecciona o arrastra tu Hoja de Vida
                  </MKTypography>
                  <MKTypography variant="body2" color="text" opacity={0.6} mt={1}>
                    Soporta archivos PDF. Máximo 5MB.
                  </MKTypography>
                </>
              )}
            </MKBox>
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Acerca de mi"
            value={resumeData.aboutMe}
            onChange={(e) => setResumeData((prev) => ({ ...prev, aboutMe: e.target.value }))}
            multiline
            minRows={4}
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { padding: '12px' } }}
          />
        </Grid>

        {/* Sección Experiencia */}
        <Grid item xs={12}>
          <MKBox display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
            <MKTypography variant="h5" fontWeight="bold" color="text">
              Experiencia laboral
            </MKTypography>
            <IconButton
              onClick={() => setOpenExp(true)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#1662c4',
                color: '#fff',
                '&:hover': { bgcolor: '#1a73e8' },
              }}
            >
              <Icon>add</Icon>
            </IconButton>
          </MKBox>
          {resumeData.experiences.length === 0 ? (
            <MKTypography variant="body2" color="text" opacity={0.7}>
              Aún no has agregado experiencia.
            </MKTypography>
          ) : (
            resumeData.experiences.map((exp) => (
              <MKBox
                key={exp.id}
                sx={{
                  mb: 3,
                  p: 3,
                  bgcolor: '#f8f9fa',
                  borderRadius: 3,
                  position: 'relative',
                }}
              >
                <IconButton
                  onClick={() => handleDeleteExperience(exp.id)}
                  size="small"
                  sx={{ position: 'absolute', top: 10, right: 10, color: '#ef5350' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <MKTypography variant="h6" fontWeight="bold">
                  {exp.cargo}{' '}
                  <span style={{ fontWeight: 'normal', fontSize: '0.9em', opacity: 0.8 }}>
                    en {exp.empresa}
                  </span>
                </MKTypography>
                <MKTypography
                  variant="button"
                  display="block"
                  color="text"
                  opacity={0.8}
                  sx={{ lineHeight: 1.2, mt: 0.5 }}
                >
                  {exp.tipoEmpleo} {exp.modalidad ? `• ${exp.modalidad}` : ''}
                </MKTypography>
                <MKTypography variant="caption" display="block" opacity={0.6} mt={0.5}>
                  {formatMonthYear(exp.fechaInicio)} -{' '}
                  {exp.actualmente ? 'Actualidad' : formatMonthYear(exp.fechaFin)}
                </MKTypography>
                {exp.descripcion && (
                  <MKTypography variant="body2" mt={1} color="text" sx={{ whiteSpace: 'pre-wrap' }}>
                    {exp.descripcion}
                  </MKTypography>
                )}
              </MKBox>
            ))
          )}
        </Grid>

        {/* Sección Educación */}
        <Grid item xs={12}>
          <MKBox display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
            <MKTypography variant="h5" fontWeight="bold" color="text">
              Educación
            </MKTypography>
            <IconButton
              onClick={() => setOpenEdu(true)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#1662c4',
                color: '#fff',
                '&:hover': { bgcolor: '#1a73e8' },
              }}
            >
              <Icon>add</Icon>
            </IconButton>
          </MKBox>
          {resumeData.education.length === 0 ? (
            <MKTypography variant="body2" color="text" opacity={0.7}>
              Aún no has agregado educación.
            </MKTypography>
          ) : (
            resumeData.education.map((edu) => (
              <MKBox
                key={edu.id}
                sx={{
                  mb: 3,
                  p: 3,
                  bgcolor: '#f8f9fa',
                  borderRadius: 3,
                  position: 'relative',
                }}
              >
                <IconButton
                  onClick={() => handleDeleteEducation(edu.id)}
                  size="small"
                  sx={{ position: 'absolute', top: 10, right: 10, color: '#ef5350' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <MKTypography variant="h6" fontWeight="bold">
                  {edu.institucion}
                </MKTypography>
                <MKTypography variant="button" display="block" color="text" opacity={0.8}>
                  {edu.titulo} {edu.disciplina ? `en ${edu.disciplina}` : ''}
                </MKTypography>
                <MKTypography variant="caption" display="block" opacity={0.6} mt={0.5}>
                  {formatMonthYear(edu.fechaInicio)} - {formatMonthYear(edu.fechaFin)}
                </MKTypography>
                {edu.descripcion && (
                  <MKTypography variant="body2" mt={1} color="text" sx={{ whiteSpace: 'pre-wrap' }}>
                    {edu.descripcion}
                  </MKTypography>
                )}
              </MKBox>
            ))
          )}
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="space-between" mt={2}>
          <MKButton
            variant="contained"
            size="large"
            onClick={() => setActiveTab(0)}
            sx={secondaryButtonSx}
          >
            Atrás
          </MKButton>
          {/* Botón de envío con indicador de carga */}
          <MKButton
            variant="contained"
            color="info"
            size="large"
            onClick={handleGuardarTodo}
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                Enviando…
              </>
            ) : (
              'Guardar'
            )}
          </MKButton>
        </Grid>
      </Grid>
    </MKBox>
  );
}

export default ResumeView;
